import { useSyncExternalStore } from 'react';
import { navigateTo } from '../app/nav.ts';
import { plural } from '../lib/format.ts';

/**
 * "Notify me when it's done": a system notification when a render batch finishes while the user
 * is in another tab or another screen. Local notifications only (the tab must stay open); the
 * service worker's notificationclick (public/notify-sw.js) focuses the app on the project.
 */
export type NotifyPermission = NotificationPermission | 'unsupported';

const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

export function notifyPermission(): NotifyPermission {
  return typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported';
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  // Picks up changes made in the browser's site settings.
  let status: PermissionStatus | null = null;
  void navigator.permissions
    ?.query({ name: 'notifications' })
    .then((s) => {
      status = s;
      s.addEventListener('change', l);
    })
    .catch(() => {});
  return () => {
    listeners.delete(l);
    status?.removeEventListener('change', l);
  };
}

export function useNotifyPermission(): NotifyPermission {
  return useSyncExternalStore(subscribe, notifyPermission, () => 'unsupported' as const);
}

export async function requestNotifyPermission(): Promise<NotifyPermission> {
  if (notifyPermission() === 'unsupported') return 'unsupported';
  await Notification.requestPermission();
  emit();
  return notifyPermission();
}

/** Clicks on notifications shown through the service worker arrive as a message. */
export function installNotificationClicks(): void {
  navigator.serviceWorker?.addEventListener('message', (e: MessageEvent) => {
    if (e.data?.type === 'open-path' && typeof e.data.path === 'string') void navigateTo({ to: e.data.path });
  });
}

export async function notifyRendersDone(opts: {
  projectId: string;
  name?: string;
  succeeded: number;
  failed: number;
}): Promise<void> {
  if (notifyPermission() !== 'granted' || opts.succeeded + opts.failed === 0) return;
  const path = `/p/${opts.projectId}/edit`;
  if (document.visibilityState === 'visible' && location.pathname === path) return; // the panel shows it
  const title = opts.name?.trim() || 'Better Lights';
  const body = opts.succeeded
    ? `${plural(opts.succeeded, 'render')} ready${opts.failed ? ` · ${opts.failed} failed` : ''}`
    : opts.failed > 1
      ? 'Your renders failed'
      : 'Your render failed';
  const options: NotificationOptions = {
    body,
    icon: '/icons/icon-192.png',
    tag: `render-${opts.projectId}`,
    data: { path },
  };
  try {
    // Android Chrome only allows notifications through the service worker.
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) return await reg.showNotification(title, options);
    const n = new Notification(title, options);
    n.onclick = () => {
      window.focus();
      void navigateTo({ to: path });
      n.close();
    };
  } catch {
    // Notifications are best-effort; the results are in the project either way.
  }
}
